// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect } from 'react';
import { useRegion } from '../context/RegionContext';
import { API_ENDPOINTS } from '../config/api';
import './OffersDialog.css';

export default function OffersDialog({ isOpen, onClose, cartItems, appliedCoupons, onApplyCoupon }) {
    const { selectedCountry } = useRegion();
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [appliedCouponCodes, setAppliedCouponCodes] = useState(new Set());

    useEffect(() => {
        // Update applied coupon codes when appliedCoupons prop changes
        if (appliedCoupons && Array.isArray(appliedCoupons)) {
            setAppliedCouponCodes(new Set(appliedCoupons.map(c => c.code)));
        }
    }, [appliedCoupons]);

    useEffect(() => {
        if (isOpen && cartItems.length > 0) {
            fetchApplicableCoupons();
        }
    }, [isOpen, cartItems]); // eslint-disable-line react-hooks/exhaustive-deps

    // Prevent body scroll when dialog is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const fetchApplicableCoupons = async () => {
        setLoading(true);
        try {
            // Get unique product IDs from cart items
            // Cart items have productId field, but id might include variants (productId_variant1:value1)
            // So we extract the base productId from id if productId is not available
            const productIdsSet = new Set();
            
            cartItems.forEach(item => {
                let productId = null;
                
                // Priority 1: Use productId if available
                if (item.productId) {
                    productId = String(item.productId);
                }
                // Priority 2: Extract from id (which might be productId_variants)
                else if (item.id) {
                    const idStr = String(item.id);
                    // If id contains underscore or colon, it might have variants
                    // Extract the base MongoDB ObjectId (24 hex characters)
                    const match = idStr.match(/^([a-fA-F0-9]{24})/);
                    if (match) {
                        productId = match[1];
                    } else {
                        // If no ObjectId pattern, take the part before first underscore or colon
                        productId = idStr.split(/[_:]/)[0];
                    }
                }
                // Priority 3: Use _id
                else if (item._id) {
                    productId = String(item._id);
                }
                
                if (productId && productId.length > 0) {
                    productIdsSet.add(productId);
                }
            });

            const productIds = Array.from(productIdsSet);

            console.log('Cart items:', cartItems);
            console.log('Fetching coupons for product IDs:', productIds);

            if (productIds.length === 0) {
                console.log('No product IDs found in cart');
                setCoupons([]);
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_ENDPOINTS.STORE.APPLICABLE_COUPONS}?country=${selectedCountry || 'IN'}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ productIds }),
            });

            console.log('Coupon API response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to fetch coupons:', errorText);
                throw new Error('Failed to fetch coupons');
            }

            const data = await response.json();
            console.log('Coupon API response data:', data);
            
            if (data.success) {
                const couponsList = data.data || [];
                console.log('Applicable coupons found:', couponsList.length);
                setCoupons(couponsList);
            } else {
                console.log('API returned success: false');
                setCoupons([]);
            }
        } catch (error) {
            console.error('Error fetching applicable coupons:', error);
            setCoupons([]);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyCoupon = async (coupon) => {
        if (!coupon || !coupon.code) {
            console.error('Invalid coupon object:', coupon);
            return;
        }

        // Ensure code is a string
        const couponCode = String(coupon.code).toUpperCase().trim();
        
        if (appliedCouponCodes.has(couponCode)) {
            return; // Already applied
        }

        // Use the existing apply coupon logic from parent
        if (onApplyCoupon) {
            await onApplyCoupon(couponCode);
            setAppliedCouponCodes(prev => new Set([...prev, couponCode]));
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="offers-dialog-backdrop fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto bg-black/50 p-[20px] backdrop-blur-[5px]" onClick={handleBackdropClick}>
            <div className="offers-dialog flex h-[50vh] max-h-[50vh] w-[50%] max-w-[600px] flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_10px_40px_rgba(0,0,0,0.2)] max-[767px]:h-[50vh] max-[767px]:max-h-[50vh] max-[767px]:w-[80%] min-[768px]:max-[1024px]:w-[70%]" onClick={(e) => e.stopPropagation()}>
                <div className="offers-dialog-header sticky top-0 z-[10] flex items-center justify-between border-b border-[#e5e7eb] bg-white px-[24px] py-[20px] max-[767px]:px-[20px] max-[767px]:py-[16px]">
                    <h2 className="offers-dialog-title m-0 text-[1.5em] font-[600] text-post-heading max-[767px]:text-[1.3em]">Offers</h2>
                    <button className="offers-dialog-close flex h-[36px] w-[36px] min-h-[36px] min-w-[36px] items-center justify-center rounded-[8px] border border-[#818181] bg-transparent p-0 text-[20px] text-orange transition-all duration-300 hover:border-orange hover:bg-orange hover:text-white" onClick={onClose} aria-label="Close">
                        <i className="fi fi-rr-cross-small"></i>
                    </button>
                </div>
                <div className="offers-dialog-content flex-1 overflow-y-auto p-0">
                    {loading ? (
                        <div className="offers-loading px-[24px] py-[40px] text-center text-[14px] text-[#6b7280]">Loading offers...</div>
                    ) : coupons.length === 0 ? (
                        <div className="offers-empty px-[24px] py-[40px] text-center text-[14px] text-[#6b7280]">No applicable offers found for your cart items.</div>
                    ) : (
                        <div className="offers-list p-0">
                            {coupons.map((coupon, index) => {
                                const isApplied = appliedCouponCodes.has(coupon.code);
                                const appliesToAll = !coupon.product || coupon.product.length === 0;
                                
                                // Get product names
                                let productNames = [];
                                if (appliesToAll) {
                                    productNames = ['All Products'];
                                } else {
                                    productNames = coupon.product.map(p => {
                                        if (p && typeof p === 'object' && p.title) {
                                            return p.title;
                                        }
                                        return 'Product';
                                    });
                                }

                                    const isPercent = coupon.discountType 
                                        ? coupon.discountType === 'PERCENT' 
                                        : coupon.couponType === true;

                                    const discountVal = isPercent
                                        ? (coupon.discountValue || coupon.offerValue || 0)
                                        : (coupon.discountByCountry && coupon.discountByCountry.length > 0 
                                            ? (() => {
                                                const found = coupon.discountByCountry.find(c => c.countryCode === selectedCountry);
                                                return found ? found.amount : coupon.discountByCountry[0].amount;
                                              })()
                                            : (coupon.offerValue || 0));
                                    const symbol = (cartItems[0] && cartItems[0].currencySymbol) || '₹';
                                    const discountDisplay = isPercent
                                        ? `${discountVal}% OFF`
                                        : `${symbol}${discountVal} OFF`;

                                    const minOrderObj = coupon.minimumOrderValueByCountry && coupon.minimumOrderValueByCountry.find(c => c.countryCode === selectedCountry);
                                    const minOrder = minOrderObj ? minOrderObj.amount : (coupon.minimumOrderValue || 0);

                                    return (
                                        <div key={index} className="offers-item flex items-start justify-between gap-[20px] border-b border-[#e5e7eb] px-[24px] py-[20px] last:border-b-0 max-[767px]:flex-col max-[767px]:gap-[12px] max-[767px]:px-[20px] max-[767px]:py-[16px]">
                                            <div className="offers-item-left flex flex-1 flex-col gap-[8px]">
                                                <div className="offers-discount mb-[4px] text-[1.2em] font-[700] text-[#059669]">{discountDisplay}</div>
                                                <div className="offers-products flex flex-col gap-[4px]">
                                                    {productNames.map((name, pIndex) => (
                                                        <div key={pIndex} className="offers-product-name text-[14px] leading-[1.4] text-[#4b5563]">{name}</div>
                                                    ))}
                                                    {minOrder > 0 && (
                                                        <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                                            Min order: {(cartItems[0] && cartItems[0].currencySymbol) || '₹'}{minOrder.toLocaleString()}
                                                        </div>
                                                    )}
                                                </div>
                                            <div className="offers-code mt-[4px] text-[13px] font-[500] text-[#6b7280]">Code: {coupon.code}</div>
                                        </div>
                                        <div className="offers-item-right flex items-center max-[767px]:w-full max-[767px]:justify-end">
                                            <button
                                                className={`offers-apply-btn min-w-[80px] whitespace-nowrap rounded-[6px] border-none px-[24px] py-[10px] text-[14px] font-[600] text-white transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-70 max-[767px]:w-full ${isApplied ? 'applied bg-[#059669] hover:bg-[#047857]' : 'bg-slider hover:-translate-y-[1px] hover:bg-post-heading'}`}
                                                onClick={() => handleApplyCoupon(coupon)}
                                                disabled={isApplied}
                                            >
                                                {isApplied ? 'Applied!' : 'Apply'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


